import React, { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { SimpleDropdown } from "./ui/simple-dropdown";
import { X, Save, User } from "lucide-react";
import { toast } from "react-hot-toast";
import { educationAPI } from "../api/axiosClient";

interface SystemAdminEditFormProps {
  isOpen: boolean;
  onClose: () => void;
  adminId: string | null;
  onSuccess: () => void;
}

const SystemAdminEditForm: React.FC<SystemAdminEditFormProps> = ({
  isOpen,
  onClose,
  adminId,
  onSuccess,
}) => {
  const handleClose = () => {
    lastLoadedAdminId.current = null;
    onClose();
  };

  const [formData, setFormData] = useState({
    id: "",
    firstName: "",
    lastName: "",
    twoFactorEnabled: false,
    userStatus: 1,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const lastLoadedAdminId = useRef<string | null>(null);

  // User status options - API enum değerlerine göre güncellendi
  const userStatuses = [
    { id: 1, label: "Aktif", value: 1 },
    { id: 2, label: "Pasif", value: 2 },
    { id: 3, label: "Doğrulama Gerekli", value: 3 },
    { id: 4, label: "Şifre Belirleme Gerekli", value: 4 },
    { id: 5, label: "Kilitli", value: 5 },
    { id: 6, label: "Askıya Alındı", value: 6 },
    { id: 7, label: "Devre Dışı", value: 7 },
    { id: 8, label: "Şifre Değiştirmeli", value: 8 },
  ];

  useEffect(() => {
    const loadSystemAdminData = async () => {
      if (!adminId) {
        return;
      }

      // Aynı ID için zaten yükleme yapıldıysa tekrar yapma
      if (lastLoadedAdminId.current === adminId) {
        return;
      }

      lastLoadedAdminId.current = adminId;
      setIsLoading(true);
      try {
        const response = await educationAPI.getSystemAdminDetails(adminId);

        if (response.success && response.data) {
          const data = response.data as any;
          setFormData({
            id: data.id || "",
            firstName: data.firstName || "",
            lastName: data.lastName || "",
            twoFactorEnabled: data.twoFactorEnabled ?? false,
            userStatus: data.userStatus ? Number(data.userStatus) : 1,
          });
        }
      } catch (error) {
        console.error("Error loading system admin data:", error);
        toast.error("Sistem yöneticisi bilgileri yüklenirken bir hata oluştu");
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen && adminId) {
      loadSystemAdminData();
    }
  }, [isOpen, adminId]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "Ad gereklidir";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Soyad gereklidir";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      console.log("Form data before request:", formData);

      const request = {
        id: formData.id,
        firstName: formData.firstName,
        lastName: formData.lastName,
        twoFactorEnabled: formData.twoFactorEnabled,
        userStatus: formData.userStatus,
      };

      console.log("Request payload:", request);

      const response = await educationAPI.updateSystemUser(
        formData.id,
        request,
      );

      if (response.success) {
        toast.success("Sistem yöneticisi başarıyla güncellendi");
        onSuccess();
        handleClose();
      } else {
        toast.error(
          response.message ||
            "Sistem yöneticisi güncellenirken bir hata oluştu",
        );
      }
    } catch (error) {
      console.error("Error updating system admin:", error);
      toast.error("Sistem yöneticisi güncellenirken bir hata oluştu");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (
    field: string,
    value: string | boolean | number,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="text-xl font-semibold">
              Sistem Yöneticisi Düzenle
            </CardTitle>
            <CardDescription>
              Sistem yöneticisi bilgilerini güncelleyin
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B51E0] mx-auto mb-2"></div>
                <p className="text-gray-500">
                  Sistem yöneticisi bilgileri yükleniyor...
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Ad Soyad */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label
                    htmlFor="firstName"
                    className="text-sm font-medium text-gray-700 mb-2 flex items-center"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Ad <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={(e) =>
                        handleInputChange("firstName", e.target.value)
                      }
                      className={`${errors.firstName ? "border-red-500" : ""}`}
                      placeholder="Ad"
                      disabled={isLoading}
                    />
                  </div>
                  {errors.firstName && (
                    <p className="text-sm text-red-500">{errors.firstName}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="lastName"
                    className="text-sm font-medium text-gray-700 mb-2 flex items-center"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Soyad <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={(e) =>
                        handleInputChange("lastName", e.target.value)
                      }
                      className={`${errors.lastName ? "border-red-500" : ""}`}
                      placeholder="Soyad"
                      disabled={isLoading}
                    />
                  </div>
                  {errors.lastName && (
                    <p className="text-sm text-red-500">{errors.lastName}</p>
                  )}
                </div>
              </div>

              {/* Kullanıcı Durumu ve İki Faktörlü Kimlik Doğrulama */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label
                    htmlFor="userStatus"
                    className="text-sm font-medium text-gray-700 mb-2 flex items-center"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Kullanıcı Durumu
                  </Label>
                  <SimpleDropdown
                    options={userStatuses}
                    value={
                      userStatuses.find(
                        (status) => status.value === formData.userStatus,
                      ) || userStatuses[0]
                    }
                    onChange={(status) => {
                      if (status) {
                        handleInputChange("userStatus", Number(status.value));
                      }
                    }}
                    placeholder="Durum seçiniz"
                    className="w-full"
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center space-x-3 pt-6">
                    <input
                      type="checkbox"
                      id="twoFactorEnabled"
                      checked={formData.twoFactorEnabled}
                      onChange={(e) =>
                        handleInputChange("twoFactorEnabled", e.target.checked)
                      }
                      className="w-4 h-4 text-[#9B51E0] bg-gray-100 border-gray-300 rounded focus:ring-[#9B51E0] focus:ring-2"
                      disabled={isLoading}
                    />
                    <label
                      htmlFor="twoFactorEnabled"
                      className="text-sm font-medium text-gray-700"
                    >
                      İki Faktörlü Kimlik Doğrulama Etkin
                    </label>
                  </div>
                </div>
              </div>

              {/* Butonlar */}
              <div className="flex justify-end space-x-3 pt-6 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting || isLoading}
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || isLoading}
                  className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                >
                  {isSubmitting ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Güncelleniyor...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Save className="w-4 h-4" />
                      <span>Kaydet</span>
                    </div>
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemAdminEditForm;
